import React, { useState } from 'react';
import NotificationSystem from "react-notification-system";
import { useAuth } from "./authContext";
import { useTranslations } from "./translationContext";
import { Result } from '../types/Result';

export interface Notification {
    title: any,
    message: any,
    level: 'info' | 'error' | 'warning' | 'success' | '',
    position: string,
    autoDismiss: number
}

interface NotificationContextValue {
    notify: (level: 'info' | 'error' | 'warning' | 'success', notification: string) => Promise<void>
    handleResult: (result: Result, showValidationMessages?: boolean) => Promise<void>
    handleResults: (results: Result[], showValidationMessages?: boolean) => Promise<void>
    handleException: (exception: any) => void
    notifications: Notification[]
    removeNotifications: (notifications: Notification[]) => void
}

const Context = React.createContext<NotificationContextValue | null>(null);

const NotificationProvider: React.FC = (props) => {
    const [notificationSystem, setNotificationSystem] = useState<NotificationSystem>();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const { user } = useAuth();
    const { translate } = useTranslations();

    const notify = async function (level: 'info' | 'error' | 'warning' | 'success', notificationMsg: string) {
        let title: any;
        switch (level) {
            case 'info':
                title = <span data-notify="icon" className="pe-7s-help1" />;
                break;
            case 'error':
                title = <span data-notify="icon" className="pe-7s-close-circle" />;
                break;
            case 'warning':
                title = <span data-notify="icon" className="pe-7s-attention" />;
                break;
            case 'success':
                title = <span data-notify="icon" className="pe-7s-check" />;
                break;

            default:
                break;
        }

        let newList: Notification[] = [];
        notifications.forEach(function (notification) { newList.push(notification); });

        translate(notificationMsg, 'Notifications', notificationMsg).then(translated => {
            newList.push({
                title: title,
                message: (<div>{translated}</div>),
                level: level,
                position: "tc",
                autoDismiss: 7
            });
            setNotifications(newList);
        });
    }

    const handleResults = async function (results: Result[], showValidationMessages?: boolean) {
        results.forEach((result) => { handleResult(result, showValidationMessages) })
    }

    const handleResult = async function (result: Result, showValidationMessages?: boolean) {
        //console.log(result);
        //console.log(notifications);

        let newList: Notification[] = [];
        notifications.forEach(function (notification) { newList.push(notification); });

        //console.log(newList);
        if (result.messages) {
            let newNotificationMessages = result.messages;

            // filter out validation messages when should not show
            if (showValidationMessages !== true)
                newNotificationMessages = result.messages.filter((x) => x.messageType !== 5 || x.memberNames.length == 0)

            newNotificationMessages.forEach(function (message) {
                let level: 'info' | 'error' | 'warning' | 'success' | '' = '';
                let title: any;
                let msg = message.message.format;
                let dismissTimeOut = 7
                switch (message.messageType) {
                    case 1:
                        level = "info";
                        title = <span data-notify="icon" className="pe-7s-help1" />;
                        break;
                    case 2:
                    case 5:
                        if (message.memberNames.length > 0 && showValidationMessages !== true)
                            return

                        level = "error"
                        title = <span data-notify="icon" className="pe-7s-close-circle" />
                        dismissTimeOut = 15
                        break;
                    case 3:
                        level = "warning";
                        title = <span data-notify="icon" className="pe-7s-attention" />;
                        break;
                    case 4:
                        level = "success";
                        title = <span data-notify="icon" className="pe-7s-check" />;
                        break;
                    default:
                        break;
                }

                translate(message.message.format, 'BusinessResultMessages', message.message).then(translated => {
                    newList.push({
                        title: title,
                        message: (<div>{translated}</div>),
                        level: level,
                        position: "tc",
                        autoDismiss: dismissTimeOut
                    });
                    setNotifications(newList);
                });
            });
        }
        //console.log(newList);
    }

    const handleException = function (exception) {
        let newList: Notification[] = [];
        notifications.forEach(function (notification) { newList.push(notification); });

        console.error(exception);

        let message: string = 'something else';
        if (exception.response) {
            /*
            * The request was made and the server responded with a
            * status code that falls out of the range of 2xx
            */
            console.log('Error response:', exception.response);
            message = exception.message + ' (' + exception.response.config.method + ': ' + exception.response.config.url + ')';

            if (exception.response.data) {
                if (typeof exception.response.data === 'string') {
                    const index = exception.response.data.indexOf(' at ')
                    if (index > 0)
                        message += ' ' + exception.response.data.substring(0, index);
                }
                else if (exception.response.data.status) {
                    message += ' ' + exception.response.data.status + ': ' + exception.response.data.title;
                }
            }

        } else if (exception.request) {
            /*
            * The request was made but no response was received, `error.request`
            * is an instance of XMLHttpRequest in the browser and an instance
            * of http.ClientRequest in Node.js
            */
            console.log('Error request:', exception.request);
            message = 'No response was received from the server.';
        } else {
            // Something happened in setting up the request and triggered an Error
            console.log('Exception: ', exception);
            message = exception.message;
        }
        newList.push({
            title: <span data-notify="icon" className="pe-7s-close-circle" />,
            message: (<div>{message}</div>),
            level: 'error',
            position: "tc",
            autoDismiss: 15
        });

        setNotifications(newList);
    }

    const removeNotifications = function (notificationsToRemove: Notification[]) {
        let newList: Notification[] = [];
        notifications.forEach(function (n) {
            let found = false;
            notificationsToRemove.forEach(function (nr) {
                if (n === nr) {
                    found = true;
                }
            });
            if (!found) {
                newList.push(n);
            }

        });
        setNotifications(newList);
    }

    return (
        <Context.Provider value={{ notify, handleResult, handleResults, handleException, notifications, removeNotifications }} {...props} />
    );
};

function useNotifications(): NotificationContextValue {
    const context = React.useContext(Context);
    if (context === null) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
}

export { NotificationProvider, useNotifications };
